/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { InboundNSIntegrationStatusEnum } from 'commons/enums';
import { IInboundInvoice } from 'commons/types';
import {
  getInProgressInboundInvoiceAction,
  updateInboundInvoiceAction,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setAssignedInboundTaskItem } from 'redux-stores/reducers/inboundTaskItemReducer';
import { AppDispatch } from 'redux-stores/store';
import { INBOUND_ROUTES } from 'routes/constants';
import { InboundInvoicePrepopulatedData } from 'services/apis/SwipeRxWmsApiInboundInvoice/type';

import { InboundListInvoiceEditForm, InboundListTable } from './components';
import S from './InboundList.styles';

export const InboundListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { inboundInvoice } = useSelector(
    (state: RootReducerInterface) => state.inboundInvoice,
  );

  const [filter, setFilter] = useState<string>('');
  const [disableProceedButton, setDisableProceedButton] =
    useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [dataFetched, setDataFetched] = useState<boolean>(false);

  const defaultDisplayedInboundInvoice: IInboundInvoice = {
    invoice_date: new Date().toISOString(),
    inbound_task_number: '',
    invoice_number: '',
    warehouse_id: 0,
    integration_status: InboundNSIntegrationStatusEnum.RECEIVING,
    receiver: '',
  };

  const [displayedInboundInvoice, setDisplayedInboundInvoice] =
    useState<IInboundInvoice>(inboundInvoice || defaultDisplayedInboundInvoice);

  const [updatedDataType, setUpdatedDataType] = useState<
    'invoice_number' | 'invoice_date'
  >('invoice_number');
  const [updatedData, setUpdatedData] = useState<string>('');

  const [isEditingInvoiceNumber, setIsEditingInvoiceNumber] =
    useState<boolean>(false);
  const [isEditingInvoiceDate, setIsEditingInvoiceDate] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(getInProgressInboundInvoiceAction()).then(() => {
      setDataFetched(true);
    });
  }, [dispatch]);

  useEffect(() => {
    dispatch(setAssignedInboundTaskItem(inboundInvoice?.items || []));
  }, [inboundInvoice, dispatch]);

  useEffect(() => {
    const ineligibleItem = inboundInvoice?.items?.find((item) => {
      const totalReceiveQty = item.receive_actual.reduce(
        (qtyReceived, receiveActual) => qtyReceived + receiveActual.quantity,
        0,
      );
      return totalReceiveQty > item.quantity || totalReceiveQty === 0;
    });
    setDisableProceedButton(!!ineligibleItem);
  }, [inboundInvoice?.items, setDisableProceedButton]);

  useEffect(() => {
    setDisplayedInboundInvoice(
      inboundInvoice || defaultDisplayedInboundInvoice,
    );
  }, [inboundInvoice, dispatch]);

  if (!inboundInvoice) {
    if (dataFetched) navigate(INBOUND_ROUTES.ASSIGN);
    return <div />;
  }

  const onFilterChange = (value: string): void => {
    setFilter(value);
    if (value === '') {
      setDisplayedInboundInvoice(inboundInvoice);
    } else {
      const filteredIdx: number[] = [];
      const filteredItems = (inboundInvoice.items || []).filter((item, idx) => {
        if (item.sku_code.includes(value)) {
          filteredIdx.push(idx);
          return true;
        }
        return false;
      });
      setDisplayedInboundInvoice({
        ...inboundInvoice,
        items: filteredItems,
      });
    }
  };

  const onSaveInboundInvoiceInfo = async (
    type: 'invoice_number' | 'invoice_date',
    params: InboundInvoicePrepopulatedData,
  ): Promise<void> => {
    dispatch(
      updateInboundInvoiceAction({
        payload: {
          invoice_date: params.invoice_date,
          invoice_number: params.invoice_number,
        },
      }),
    );
    setOpenModal(false);
    if (type === 'invoice_number') {
      setIsEditingInvoiceNumber(false);
    } else {
      setIsEditingInvoiceDate(false);
    }
    dispatch(getInProgressInboundInvoiceAction());
  };

  const onCancelUpdateInboundInvoiceInfo = (
    type: 'invoice_number' | 'invoice_date',
  ): void => {
    const clonedState = structuredClone(displayedInboundInvoice);
    if (type === 'invoice_number') {
      clonedState.invoice_number = inboundInvoice.invoice_number;
      setIsEditingInvoiceNumber(false);
    } else {
      clonedState.invoice_date = inboundInvoice.invoice_date;
      setIsEditingInvoiceDate(false);
    }
    setDisplayedInboundInvoice(clonedState);
    setOpenModal(false);
  };

  return (
    <div>
      <S.ContentWrapper>
        <S.SearchBar
          placeholder="SKU Number or Scan"
          variant="outlined"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          inputProps={{
            'data-testid': 'InputListSKUInbound',
          }}
        />

        <InboundListInvoiceEditForm
          displayedInboundInvoice={displayedInboundInvoice}
          setDisplayedInboundInvoice={setDisplayedInboundInvoice}
          setUpdatedDataType={setUpdatedDataType}
          setUpdatedData={setUpdatedData}
          isEditingInvoiceNumber={isEditingInvoiceNumber}
          isEditingInvoiceDate={isEditingInvoiceDate}
          setIsEditingInvoiceNumber={setIsEditingInvoiceNumber}
          setIsEditingInvoiceDate={setIsEditingInvoiceDate}
          setOpenModal={setOpenModal}
        />

        <InboundListTable
          inboundTaskItems={displayedInboundInvoice.items || []}
        />
        <S.BlankWhiteSpace />
      </S.ContentWrapper>
      {/* Footer Section */}
      <S.FooterWrapper>
        <S.PrimaryButton
          disabled={disableProceedButton}
          onClick={() => navigate(INBOUND_ROUTES.SUMMARY)}
          data-testid="BtnInboundListProceed"
        >
          PROCEED
        </S.PrimaryButton>
      </S.FooterWrapper>
      <ConfirmationDialog
        title="EDIT INVOICE"
        content={`Are you sure to update ${updatedDataType.replace(
          '_',
          ' ',
        )} to ${updatedData}?`}
        onClose={() => setOpenModal(false)}
        openModal={openModal}
        onConfirm={() =>
          onSaveInboundInvoiceInfo(updatedDataType, {
            invoice_number: displayedInboundInvoice.invoice_number,
            invoice_date: displayedInboundInvoice.invoice_date,
          })
        }
        onCancel={() => onCancelUpdateInboundInvoiceInfo(updatedDataType)}
      />
    </div>
  );
};
